import { ACHIEVEMENTS } from "@dailywars/core/achievements";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { PageHeader } from "#/components/PageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#/components/ui/card";
import { Skeleton } from "#/components/ui/skeleton";
import { DOCUMENT_TITLES, documentHead } from "#/lib/documentTitle";
import { playerAchievementsQueryOptions } from "#/lib/queries";

import { AchievementList, OverallAchievementProgress } from "./-profile.achievements";
import { ProfilePageScaffold } from "./-profile.scaffold";

export const Route = createFileRoute("/profile_/$name_/achievements")({
  head: () => documentHead(DOCUMENT_TITLES.achievements),
  loader: async ({ context, params: { name } }) => {
    await context.queryClient.ensureQueryData(playerAchievementsQueryOptions(name));
    return { name };
  },
  pendingComponent: AchievementsPageLoading,
  component: AchievementsPage,
});

function AchievementsPageLoading() {
  return (
    <ProfilePageScaffold eyebrow="Achievements" title={<Skeleton className="h-12 w-64" />}>
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-4 w-56" />
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-14 w-full" />
          ))}
        </CardContent>
      </Card>
    </ProfilePageScaffold>
  );
}

function AchievementsPage() {
  const { name } = Route.useLoaderData();
  const { data } = useSuspenseQuery(playerAchievementsQueryOptions(name));
  const earnedCount = data.achievements.filter(({ earnedAt }) => earnedAt !== undefined).length;
  const totalCount = ACHIEVEMENTS.length;

  return (
    <ProfilePageScaffold eyebrow="Achievements" title={<PageHeader>{data.displayName}</PageHeader>}>
      <Card>
        <CardHeader>
          <CardTitle>Achievement collection</CardTitle>
          <CardDescription>
            {earnedCount} of {totalCount} achievements earned.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <OverallAchievementProgress earnedCount={earnedCount} totalCount={totalCount} />
          <AchievementList
            achievements={data.achievements}
            achievementValues={data.achievementValues}
            achievementEarnCounts={data.achievementEarnCounts}
          />
        </CardContent>
      </Card>
    </ProfilePageScaffold>
  );
}
